import { useNavigate } from "react-router-dom";
import EditableText from "../ui/EditableText";

export type FilingType = {
  title: string
  image: string
  text: string
  path: string
}

const defaultFilingTypes: FilingType[] = [
  {
    title: "Trademark",
    image: "/images/trademark.svg",
    text: "Protect your brand across the European Union. File an EU trademark online with professional representation before the EUIPO, without the complexity of managing the European filing process yourself.",
    path: "/trademark",
  },
  {
    title: "Design",
    image: "/images/design.svg",
    text: "Protect your design in Europe, simply and efficiently. File your EU design online and let our European Patent Attorneys take care of the official filing and representation before the EUIPO.",
    path: "/design",
  },
  {
    title: "Patent",
    image: "/images/patent.svg",
    text: "File your European patent application online. Provide your application information and documents online. We prepare and submit your application and professionally represent you before the European Patent Office.",
    path: "/patent",
  },
];

interface InstantFilingSectionProps {
  id?: string;
  heading?: string;
  types?: FilingType[];
  closingDivider?: boolean;
  /** Site-content editing pilot (Home page only): when set, each card's
   * text becomes a click-to-edit field instead of static text. */
  editable?: boolean;
  onSaveCardText?: (index: number, value: string) => void;
  onSaveCardTitle?: (index: number, value: string) => void;
  onSaveHeading?: (value: string) => void;
}

function splitLead(text: string): { lead: string; rest: string } {
  const splitIndex = text.indexOf(". ");
  if (splitIndex === -1) {
    return { lead: text, rest: "" };
  }
  return { lead: text.slice(0, splitIndex + 1), rest: text.slice(splitIndex + 2) };
}

export default function InstantFilingSection({
  id = "instant-filing",
  heading = "INSTANT FILING",
  types = defaultFilingTypes,
  closingDivider = true,
  editable = false,
  onSaveCardText,
  onSaveCardTitle,
  onSaveHeading,
}: InstantFilingSectionProps) {
  const navigate = useNavigate();
  const filingTypes = types;

  return (
    <section id={id} className="bg-white px-4 py-16 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto w-11/12 border-t border-dantzBlue" />

        <EditableText
          as="h2"
          value={heading}
          editMode={editable}
          onSave={(value) => onSaveHeading?.(value)}
          className="my-12 block text-center text-[28px] font-light tracking-widest text-dantzBlue"
        />

        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          {filingTypes.map((type, index) => {
            const { lead, rest } = splitLead(type.text);
            return (
              <div key={type.title} className="relative">
                {index > 0 && (
                  <div className="absolute left-[-1.25rem] top-0 hidden h-80 w-px bg-dantzBlue md:block" />
                )}

                <img
                  src={type.image}
                  alt={type.title}
                  className="h-40 w-full object-cover"
                />

                {editable ? (
                  <EditableText
                    as="h3"
                    value={type.title}
                    editMode
                    onSave={(value) => onSaveCardTitle?.(index, value)}
                    className="font-heading mt-4 text-[20px] font-normal uppercase tracking-wide text-dantzBlue"
                  />
                ) : (
                  <h3 className="font-heading mt-4 text-[20px] font-normal tracking-wide text-dantzBlue">
                    {type.title.toUpperCase()}
                  </h3>
                )}

                {editable ? (
                  <EditableText
                    as="p"
                    value={type.text}
                    editMode
                    multiline
                    onSave={(value) => onSaveCardText?.(index, value)}
                    className="font-heading mt-3 text-sm text-gray-600"
                  />
                ) : (
                  <>
                    <p className="font-heading mt-0.5 text-sm text-gray-500">{lead}</p>

                    {rest && (
                      <p className="font-heading mt-3 text-sm text-gray-600">{rest}</p>
                    )}
                  </>
                )}

                <button
                  onClick={() => navigate(type.path)}
                  className="mt-4 block"
                  aria-label="Read more"
                >
                  <img src="/images/btn-read-more.svg" alt="Read more" />
                </button>
              </div>
            );
          })}
        </div>

        {closingDivider && (
          <div className="mx-auto mt-12 w-11/12 border-t border-dantzBlue" />
        )}
      </div>
    </section>
  );
}
